import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface Contact {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  tags?: string[];
}

interface FlowExecution {
  id: string;
  flow_id: string;
  flow_name?: string;
  flow_type?: string;
}

interface CallItem {
  call_id: string;
  original_request?: {
    contact_id?: string;
    flow_id?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface ContactCallStats {
  contactId: string;
  contactName: string;
  phoneNumber: string;
  email: string;
  tags: string[];
  flowName: string;
  flowType: string;
  call_id: string;
}

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface UseContactsCalledProps {
  isOpen: boolean;
  organization_id: string;
  dateRange: DateRange;
  pageSize?: number;
  getFilteredCalls: (
    type: 'all' | 'answered' | 'opportunities',
    sortField?: 'callTimestamp' | 'corrected_duration' | 'answered_by',
    sortDirection?: 'asc' | 'desc'
  ) => Promise<unknown[]>;
  showAllRecords?: boolean;
}

const PAGE_SIZE = 25;

export const useContactsCalled = ({
  isOpen,
  organization_id,
  dateRange,
  pageSize = PAGE_SIZE,
  getFilteredCalls,
  showAllRecords = false,
}: UseContactsCalledProps) => {
  const [allCalls, setAllCalls] = useState<CallItem[]>([]);
  const [stats, setStats] = useState<ContactCallStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastDoc, setLastDoc] = useState<CallItem | null>(null);
  const [hasNext, setHasNext] = useState(true);
  const [_totalStats, setTotalStats] = useState(0);

  const processPageData = useCallback(async (pageCalls: CallItem[]) => {
    // Extract unique IDs
    const contactIds = [
      ...new Set(pageCalls.map((call) => call.original_request?.contact_id).filter(Boolean)),
    ];
    const callIds = pageCalls.map((call) => call.call_id).filter(Boolean);

    // Fetch contacts
    const contactsData: { [key: string]: Contact } = {};
    const batchSize = 10;

    for (let i = 0; i < contactIds.length; i += batchSize) {
      const batchIds = contactIds.slice(i, i + batchSize);
      const contactsRef = collection(db, 'Contacts');
      const contactsQuery = query(contactsRef, where(documentId(), 'in', batchIds));
      const snapshot = await getDocs(contactsQuery);

      snapshot.forEach((doc) => {
        const data = doc.data();
        contactsData[doc.id] = {
          id: doc.id,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phoneNumber: data.phoneNumber,
          tags: data.tags || [],
        };
      });
    }

    // Fetch flow executions
    const flowExecutionsData: { [key: string]: FlowExecution } = {};

    for (let i = 0; i < callIds.length; i += batchSize) {
      const batchIds = callIds.slice(i, i + batchSize);
      const executionsRef = collection(db, 'FlowExecutions');
      const executionsQuery = query(executionsRef, where('call_id', 'in', batchIds));
      const snapshot = await getDocs(executionsQuery);

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.call_id) {
          flowExecutionsData[data.call_id] = {
            id: doc.id,
            flow_id: data.flow_id,
            flow_name: data.flow_name,
            flow_type: data.flow_type,
          };
        }
      });
    }

    // Build stats array for this page
    return pageCalls.map((call) => {
      const contactId = call.original_request?.contact_id;
      const contact = contactId ? contactsData[contactId] : undefined;
      const flowExecution = flowExecutionsData[call.call_id];

      return {
        contactId: contactId || '',
        contactName: contact
          ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || '-'
          : '-',
        phoneNumber: contact?.phoneNumber || '-',
        email: contact?.email || '-',
        tags: contact?.tags || [],
        flowName: flowExecution?.flow_name || '-',
        flowType: flowExecution?.flow_type || '-',
        call_id: call.call_id,
      };
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!organization_id || !isOpen || !dateRange.from || !dateRange.to) {
      setStats([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const allCalls = (await getFilteredCalls('all', 'callTimestamp', 'desc')) as CallItem[];
      setAllCalls(allCalls);
      setTotalStats(allCalls.length);

      // Process first page or all calls if showAllRecords is true
      const callsToProcess = showAllRecords ? allCalls : allCalls.slice(0, pageSize);
      const statsArray = await processPageData(callsToProcess);
      setStats(statsArray);

      setHasNext(!showAllRecords && allCalls.length > pageSize);
      if (callsToProcess.length > 0 && !showAllRecords) {
        setLastDoc(callsToProcess[callsToProcess.length - 1]);
      }
    } catch (err) {
      console.error('Error fetching contact stats:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setLoading(false);
    }
  }, [
    organization_id,
    isOpen,
    dateRange,
    pageSize,
    getFilteredCalls,
    processPageData,
    showAllRecords,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, showAllRecords]);

  const loadMore = useCallback(async () => {
    if (!lastDoc || loading || !hasNext) return;

    setLoading(true);
    try {
      const lastIndex = allCalls.findIndex((call) => call.call_id === lastDoc.call_id);
      if (lastIndex === -1) return;

      const nextCalls = allCalls.slice(lastIndex + 1, lastIndex + 1 + pageSize);
      if (nextCalls.length === 0) {
        setHasNext(false);
        return;
      }

      const remainingAfterLoad = allCalls.length - (stats.length + nextCalls.length);
      setHasNext(remainingAfterLoad > 0);

      const newStats = await processPageData(nextCalls);
      setStats((prev) => [...prev, ...newStats]);

      if (nextCalls.length > 0) {
        setLastDoc(nextCalls[nextCalls.length - 1]);
      }
    } catch (err) {
      console.error('Error loading more:', err);
      setError(err instanceof Error ? err : new Error('Failed to load more'));
    } finally {
      setLoading(false);
    }
  }, [lastDoc, loading, hasNext, allCalls, stats.length, pageSize, processPageData]);

  return {
    stats,
    setStats,
    loading,
    error,
    hasNext,
    loadMore,
  };
};
